// tutorService.js
import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore"
import { db } from "@/firebase"
import { logError } from "@/utils/secureLogger"

class TutorService {
  // Get a tutor by document ID (from users collection)
  async getTutorById(userId) {
    try {
      const userRef = doc(db, "users", userId)
      const userSnap = await getDoc(userRef)

      if (!userSnap.exists()) {
        throw new Error("User not found")
      }

      const userData = userSnap.data()

      if (userData.userType !== "tutor") {
        throw new Error("User is not a tutor")
      }

      return { id: userSnap.id, ...userData }
    } catch (error) {
      logError("Error fetching tutor details from Firestore", error instanceof Error ? error : new Error(String(error)))
      throw error
    }
  }

  // Fetch all tutors
  async getAllTutors() {
    try {
      const q = query(collection(db, "users"), where("userType", "==", "tutor"))
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    } catch (error) {
      logError("Error fetching tutors from Firestore", error instanceof Error ? error : new Error(String(error)))
      throw error
    }
  }

  // ✅ Fetch availability subcollection for a tutor
  async getTutorAvailability(tutorId) {
    try {
      const availabilityRef = collection(db, "users", tutorId, "availability")
      const snapshot = await getDocs(availabilityRef)

      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    } catch (error) {
      logError("Error fetching tutor availability", error instanceof Error ? error : new Error(String(error)))
      throw error
    }
  }
}

// Lazy singleton instance to avoid circular dependency issues
let tutorServiceInstance = null;

function getTutorService() {
  if (!tutorServiceInstance) {
    tutorServiceInstance = new TutorService();
  }
  return tutorServiceInstance;
}

// For backward compatibility, export a proxy that lazily initializes
export const tutorService = new Proxy({}, {
  get(target, prop) {
    return getTutorService()[prop];
  }
});
